package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.PurchasesExtraReason
import com.composables.icons.lucide.Ban
import com.composables.icons.lucide.Check
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Pencil
import com.composables.icons.lucide.Plus
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_action_cancel
import portalvv.shared.generated.resources.compras_action_close
import portalvv.shared.generated.resources.compras_action_save
import portalvv.shared.generated.resources.compras_extra_reason_activate
import portalvv.shared.generated.resources.compras_extra_reason_active
import portalvv.shared.generated.resources.compras_extra_reason_create_title
import portalvv.shared.generated.resources.compras_extra_reason_edit_title
import portalvv.shared.generated.resources.compras_extra_reason_inactivate
import portalvv.shared.generated.resources.compras_extra_reason_inactive
import portalvv.shared.generated.resources.compras_extra_reason_name
import portalvv.shared.generated.resources.compras_extra_reason_new
import portalvv.shared.generated.resources.compras_extra_reasons_empty
import portalvv.shared.generated.resources.compras_extra_reasons_title

@Composable
fun PurchasesExtraReasonsDialog(
    reasons: List<PurchasesExtraReason>,
    loading: Boolean,
    error: String?,
    canCreate: Boolean,
    canEdit: Boolean,
    onCreate: (String) -> Unit,
    onRename: (String, String) -> Unit,
    onChangeStatus: (String, Boolean) -> Unit,
    onDismissError: () -> Unit,
    onDismiss: () -> Unit,
) {
    var creating by remember { mutableStateOf(false) }
    var editing by remember { mutableStateOf<PurchasesExtraReason?>(null) }
    val dismiss = {
        onDismissError()
        onDismiss()
    }

    if (creating || editing != null) {
        val target = editing
        PurchasesExtraReasonFormDialog(
            reason = target,
            error = error,
            onSave = { name ->
                if (target == null) {
                    onCreate(name)
                } else {
                    onRename(target.id, name)
                }
                creating = false
                editing = null
            },
            onDismissError = onDismissError,
            onCancel = {
                creating = false
                editing = null
            },
        )
    } else {
        AppModalCard(
            title = stringResource(Res.string.compras_extra_reasons_title),
            onDismiss = dismiss,
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().widthIn(max = 560.dp),
                verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            ) {
                if (canCreate) {
                    AppButton(
                        text = stringResource(Res.string.compras_extra_reason_new),
                        onClick = {
                            onDismissError()
                            creating = true
                        },
                        variant = PortalButtonVariant.Secondary,
                        leadingIcon = Lucide.Plus,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
                error?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.error,
                    )
                }
                when {
                    loading && reasons.isEmpty() -> {
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
                            horizontalArrangement = Arrangement.Center,
                        ) {
                            CircularProgressIndicator()
                        }
                    }
                    reasons.isEmpty() -> {
                        Text(
                            text = stringResource(Res.string.compras_extra_reasons_empty),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                    else -> {
                        reasons.forEach { reason ->
                            ReasonRow(
                                reason = reason,
                                canEdit = canEdit,
                                onEdit = {
                                    onDismissError()
                                    editing = reason
                                },
                                onToggleStatus = { onChangeStatus(reason.id, !reason.active) },
                            )
                        }
                    }
                }
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    AppButton(
                        text = stringResource(Res.string.compras_action_close),
                        onClick = dismiss,
                        variant = PortalButtonVariant.Outlined,
                    )
                }
            }
        }
    }
}

@Composable
private fun ReasonRow(
    reason: PurchasesExtraReason,
    canEdit: Boolean,
    onEdit: () -> Unit,
    onToggleStatus: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = reason.name,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Medium,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                Text(
                    text =
                        stringResource(
                            if (reason.active) {
                                Res.string.compras_extra_reason_active
                            } else {
                                Res.string.compras_extra_reason_inactive
                            },
                        ),
                    style = MaterialTheme.typography.bodySmall,
                    color =
                        if (reason.active) {
                            MaterialTheme.colorScheme.primary
                        } else {
                            MaterialTheme.colorScheme.onSurfaceVariant
                        },
                )
            }
            if (canEdit) {
                IconButton(onClick = onEdit) {
                    Icon(
                        imageVector = Lucide.Pencil,
                        contentDescription = stringResource(Res.string.compras_extra_reason_edit_title),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                IconButton(onClick = onToggleStatus) {
                    Icon(
                        imageVector = if (reason.active) Lucide.Ban else Lucide.Check,
                        contentDescription =
                            stringResource(
                                if (reason.active) {
                                    Res.string.compras_extra_reason_inactivate
                                } else {
                                    Res.string.compras_extra_reason_activate
                                },
                            ),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
    }
}

@Composable
private fun PurchasesExtraReasonFormDialog(
    reason: PurchasesExtraReason?,
    error: String?,
    onSave: (String) -> Unit,
    onDismissError: () -> Unit,
    onCancel: () -> Unit,
) {
    var name by remember(reason) { mutableStateOf(reason?.name.orEmpty()) }
    AppModalCard(
        title =
            stringResource(
                if (reason == null) {
                    Res.string.compras_extra_reason_create_title
                } else {
                    Res.string.compras_extra_reason_edit_title
                },
            ),
        onDismiss = onCancel,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().widthIn(max = 480.dp),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            AppTextField(
                value = name,
                onValueChange = {
                    name = it
                    onDismissError()
                },
                label = stringResource(Res.string.compras_extra_reason_name),
                modifier = Modifier.fillMaxWidth(),
            )
            error?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.error,
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.compras_action_cancel),
                    onClick = onCancel,
                    variant = PortalButtonVariant.Outlined,
                )
                AppButton(
                    text = stringResource(Res.string.compras_action_save),
                    onClick = { onSave(name.trim()) },
                    enabled = name.isNotBlank(),
                )
            }
        }
    }
}
